import React, { useCallback, useState } from 'react';
import { MainWrapper } from './components/mainWrapper/MainWrapper';
import { Navigate, Route, Routes } from 'react-router-dom';
import RouteChangeTracker from './core/RouteChangeTracker';
import './App.scss';
import { UserManagement } from './components/admin/UserManagement';
import { CaptureDashboardContainer } from './components/CaptureDashboardPage/CaptureDashboardContainer';
import { HelpPage } from './components/helpPage/HelpPage';
import { LandingPage } from './components/landingPage/LandingPage';
import { LogOffLoader } from './components/logoffLoader/LogOffLoader';
import { RedirectPage } from './components/redirectPage/RedirectPage';
import { ProgressReporting } from './components/welcomePage/ProgressReporting';
import { WelcomePage } from './components/welcomePage/WelcomePage';
import { useAuth } from 'react-oidc-context';
import LookupFactory, { LookupsContext } from './lookups/LookupFactory';
import { useLookupTablesLocal } from './core/UseAPILocal';
import BmpGeneral from './models/BmpGeneral';
import { SidebarContext, SidebarManagerAction, SidebarManagerState, SidebarTypes } from './core/SidebarComponent';
import { Participants } from './components/welcomePage/Participants';
import { Downloads } from './components/welcomePage/Downloads';
import { Dashboard } from './components/welcomePage/Dashboard';
import { CoBenefits } from './components/welcomePage/CoBenefits';
import { ManageProjectData } from './components/reporting/manageProjectData/ManageProjectData';
import { BuildWatershedForm } from './components/reporting/buildWatershedForm/BuildWatershedForm';
import { BuildAnnReportForm } from './components/reporting/buildAnnReportForm/BuildAnnReportForm';

export const LoadingContext = React.createContext({
    loading: false,
    setLoading: (loading: boolean) => { }
});

export type ReportingPageType = "alertMessagePage" |
    "none" |
    "manageProjectData" |
    "buildWatershedForm" |
    "buildAnnReportForm" |
    "configureMilestones" |
    "configureProjAssumptions" |
    "configurePublicDash" |
    "userManagement";

function App() {
    const auth = useAuth();

    const [loading, setLoading] = useState(true);
    const [lookups, setLookups] = useState<LookupFactory>(null);
    const [fetchingLookups, setFetchingLookups] = useState<boolean>(true);
    const [refreshTable, setRefreshTable] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);
    const [sidebarProjectInfoObj, setSidebarProjectInfoObj] = useState<BmpGeneral>(null);
    const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
    const [reportPageToShow, setReportPageToShow] = useState<ReportingPageType>("none");

    useLookupTablesLocal(auth.user?.access_token, setFetchingLookups, fetchingLookups, setLookups);

    const [sidebarStore, setSidebarStore] = React.useState<SidebarManagerState>({
        current: { name: "Reports" as SidebarTypes, content: null },
        previous: { name: "Welcome" as SidebarTypes, content: null },
    });

    const dispatchSidebar = useCallback((action: SidebarManagerAction) => {
        setSidebarStore(sidebarStore => sidebarReducer(sidebarStore, action));
    }, []);

    const sidebarReducer = (
        state: SidebarManagerState,
        action: SidebarManagerAction
    ) => {
        switch (action.type) {
            case "open":
                return {
                    current: action.payload,
                    previous:
                        action.payload.name === state.current.name
                            ? state.previous
                            : state.current,
                };
            case "close":
                return {
                    current: state.previous,
                    previous: { name: "Home" as SidebarTypes, content: null },
                };
            case "toggle":
                return {
                    current:
                        state.current.name === action.payload.name
                            ? state.previous
                            : action.payload,
                    previous:
                        state.current.name === action.payload.name
                            ? { name: "Home" as SidebarTypes, content: null }
                            : state.current,
                };
            case "reset":
                return {
                    current: { name: "Home" as SidebarTypes, content: null },
                    previous: { name: "Home" as SidebarTypes, content: null },
                };
            default:
                throw new Error();
        }
    };
    return (
        <div className="App">
            <LookupsContext.Provider value={[lookups, () => setFetchingLookups(true)]}>
                <SidebarContext.Provider value={{
                    sidebarStore,
                    dispatchSidebar,
                    sidebarProjectInfoObj,
                    setSidebarProjectInfoObj,
                    sidebarVisible: sidebarVisible,
                    setSidebarVisible: setSidebarVisible
                }}>
                    <LoadingContext.Provider value={{ loading: loading, setLoading: setLoading }}>

                        <Routes>
                            <Route path="/" element={<MainWrapper setReportPageToShow={setReportPageToShow} reportPageToShow={reportPageToShow} />}>
                                <Route index element={<AutoRedirect />} />
                                <Route path="authentication/callback" element={<AutoRedirect />} />
                                <Route path="welcome" element={<WelcomePage />} />
                                <Route path="capture" element={<CaptureDashboardContainer />} />
                                <Route path="cobenefits" element={<CoBenefits />} />
                                <Route path="progress-reporting" element={<Participants />}>
                                    <Route path=":wmgIdParam" element={<ProgressReporting />}>
                                        <Route index element={<Navigate to="downloads" />} />
                                        <Route path="downloads" element={<Downloads />} />
                                        <Route path="dashboard" element={<Dashboard />} />
                                    </Route>
                                </Route>
                                <Route
                                    path="/reports"
                                    element={
                                        <LandingPage />
                                    }>
                                    <Route index element={<Navigate to="manage-project-data" />} />
                                    <Route path="manage-project-data" element={<ManageProjectData refreshTable={refreshTable} setRefreshTable={setRefreshTable} />} />
                                    <Route path="build-watershed-form" element={<BuildWatershedForm />} />
                                    <Route path="build-ann-report-form" element={<BuildAnnReportForm />} />
                                </Route>
                                <Route path="/userManagement" element={<UserManagement />} />
                                <Route path="/help" element={<HelpPage lookups={lookups} />} />
                            </Route>
                            <Route path="/logoff" element={<LogOffLoader />} />
                            <Route path="*" element={<AutoRedirect />} />
                            <Route path="/redirect" element={<RedirectPage />} />
                        </Routes>
                    </LoadingContext.Provider>
                </SidebarContext.Provider>
            </LookupsContext.Provider>
            <RouteChangeTracker />
        </div >
    );
}

function AutoRedirect() {
    const auth = useAuth();

    console.log("auto redirect:", auth);

    return <>
        {(auth && auth.user) && <Navigate replace to="/reports" />}
        {!(auth && auth.user) && <Navigate replace to="/welcome" />}
    </>
}

export default App;
